<template>
  <div class="p-1">
    <!-- Log Viewer Header -->
    <v-card class="rounded-xl border border-slate-100 shadow-sm overflow-hidden mb-4" flat>
      <v-card-text class="p-6">
        <div class="flex items-start gap-4">
          <v-avatar color="white" class="border border-slate-100 p-1" size="48">
            <img :src="ImgSms" class="w-10 h-10 object-contain" />
          </v-avatar>
          <div class="flex flex-col">
            <span class="text-sm font-bold text-slate-800 leading-tight">
              Quickberry SMS Integration Logs
            </span>
            <span class="text-xs text-slate-500 mt-1 leading-normal">
              Monitor, search, and audit transaction logs for automated SMS notifications, verification codes, and client messages sent via the Quickberry gateway.
            </span>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Filters & Actions Header -->
    <v-card class="mb-4 rounded-xl border border-slate-100 shadow-sm" flat>
      <v-card-text class="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <!-- Left: Search and Filters -->
        <div class="flex flex-wrap items-center gap-3 flex-grow max-w-4xl">
          <v-text-field
            v-model="search"
            placeholder="Search by Mobile, Message or Message ID..."
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-magnify"
            class="rounded-lg max-w-sm bg-slate-50 text-xs"
            @keyup.enter="fetchLogs"
            clearable
            @click:clear="clearSearch"
          ></v-text-field>

          <v-select
            v-model="selectedStatus"
            :items="statusOptions"
            label="Status"
            outlined
            dense
            hide-details
            class="rounded-lg max-w-[200px] bg-slate-50 text-xs"
            @change="fetchLogs"
          ></v-select>
        </div>

        <!-- Right: Actions -->
        <div class="flex justify-end items-center gap-2">
          <v-btn
            color="indigo"
            outlined
            class="normal-case rounded-lg text-xs"
            @click="fetchLogs"
            :loading="loading"
          >
            <v-icon left size="16">mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Table Card -->
    <v-card class="rounded-xl border border-slate-100 shadow-sm overflow-hidden" flat>
      <v-simple-table class="text-xs">
        <template v-slot:default>
          <thead>
            <tr
              class="bg-slate-50/75 border-b border-slate-100 text-slate-500 font-semibold text-[11px] uppercase tracking-wider"
            >
              <th class="py-3 px-4 text-left">Message ID</th>
              <th class="py-3 px-4 text-left">Recipient Mobile</th>
              <th class="py-3 px-4 text-left">Message Content</th>
              <th class="py-3 px-4 text-center">Status</th>
              <th class="py-3 px-4 text-left">Timestamp</th>
              <th class="py-3 px-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody v-if="!loading && logs.length">
            <tr
              v-for="log in logs"
              :key="log._id"
              class="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
            >
              <!-- Message ID -->
              <td class="py-3 px-4 font-mono text-[10px] text-slate-500 max-w-[150px] truncate">
                {{ log.messageId || 'N/A' }}
              </td>

              <!-- Recipient Mobile -->
              <td class="py-3 px-4 text-slate-600 font-medium">
                {{ log.mobile }}
              </td>

              <!-- Message Content -->
              <td class="py-3 px-4 text-slate-700 font-medium max-w-[300px] truncate">
                {{ log.message }}
              </td>

              <!-- Status Badge -->
              <td class="py-3 px-4 text-center">
                <v-chip
                  :color="getBadgeColor(log.status)"
                  class="white--text font-bold text-[9px] uppercase px-2.5 py-0.5 rounded-full"
                  small
                >
                  {{ log.status }}
                </v-chip>
              </td>

              <!-- Timestamp -->
              <td class="py-3 px-4 text-slate-500">
                {{ formatDateTime(log.timestamp) }}
              </td>

              <!-- Actions -->
              <td class="py-3 px-4 text-center">
                <v-btn
                  icon
                  color="indigo"
                  small
                  @click="openDetails(log)"
                  class="hover:bg-indigo-50 rounded-lg"
                >
                  <v-icon size="16">mdi-eye-outline</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
          <tbody v-else-if="loading">
            <tr>
              <td colspan="6" class="py-8 text-center">
                <v-progress-circular indeterminate color="indigo" size="24"></v-progress-circular>
                <div class="text-slate-400 mt-2 font-medium">Loading Quickberry SMS Logs...</div>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="6" class="py-12 text-center text-slate-400 font-medium">
                <v-icon size="40" class="mb-2 text-slate-300">mdi-message-alert-outline</v-icon>
                <div>No SMS logs found</div>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <!-- Pagination Footer -->
      <div
        v-if="logs.length"
        class="p-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white"
      >
        <div class="text-xs font-semibold text-slate-500">
          Showing {{ (page - 1) * limit + 1 }} to {{ Math.min(page * limit, totalRecords) }} of
          {{ totalRecords }} records
        </div>
        <div class="flex items-center gap-4">
          <v-select
            v-model="limit"
            :items="[10, 20, 50, 100]"
            outlined
            dense
            hide-details
            class="rounded-lg max-w-[80px] text-xs bg-slate-50"
            @change="changeLimit"
          ></v-select>
          <div class="flex items-center gap-1">
            <v-btn
              icon
              small
              class="border border-slate-200 rounded-lg hover:bg-slate-50"
              :disabled="page === 1"
              @click="prevPage"
            >
              <v-icon size="16">mdi-chevron-left</v-icon>
            </v-btn>
            <span class="text-xs font-bold text-slate-600 px-2"
              >Page {{ page }} of {{ totalPages }}</span
            >
            <v-btn
              icon
              small
              class="border border-slate-200 rounded-lg hover:bg-slate-50"
              :disabled="page === totalPages"
              @click="nextPage"
            >
              <v-icon size="16">mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="650px" scrollable>
      <v-card class="rounded-2xl border border-slate-100 shadow-xl bg-slate-50 flex flex-col p-4">
        <!-- Dialog Header -->
        <v-card-title
          class="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between rounded-t-xl"
        >
          <div class="flex items-center gap-3">
            <v-avatar color="white" class="border border-slate-100 p-1" size="40">
              <img :src="ImgSms" class="w-8 h-8 object-contain" />
            </v-avatar>
            <div class="flex flex-col">
              <span class="text-sm font-bold text-slate-800 leading-tight">
                SMS Transaction Details
              </span>
              <span
                class="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-0.5"
              >
                {{ activeLog.messageId || 'No Message ID' }}
              </span>
            </div>
          </div>
          <v-btn
            icon
            color="slate"
            @click="detailsDialog = false"
            class="hover:bg-slate-100 rounded-lg"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Dialog Content -->
        <v-card-text class="flex-grow p-6 overflow-y-auto flex flex-col gap-5">
          <!-- Overview Cards -->
          <div class="grid grid-cols-2 gap-3">
            <div class="bg-white p-3 rounded-xl border border-slate-100 flex flex-col gap-0.5">
              <span class="text-[9px] uppercase font-bold text-slate-400 tracking-wider"
                >Delivery Status</span
              >
              <div class="flex items-center mt-1">
                <v-chip
                  :color="getBadgeColor(activeLog.status)"
                  class="white--text font-bold text-[10px] uppercase"
                  x-small
                >
                  {{ activeLog.status }}
                </v-chip>
              </div>
            </div>
            <div class="bg-white p-3 rounded-xl border border-slate-100 flex flex-col gap-0.5">
              <span class="text-[9px] uppercase font-bold text-slate-400 tracking-wider"
                >Sent At</span
              >
              <span class="text-xs font-semibold text-slate-700 mt-1">
                {{ formatDateTime(activeLog.timestamp) }}
              </span>
            </div>
          </div>

          <!-- Message Info Card -->
          <div class="bg-white p-4 rounded-xl border border-slate-100 flex flex-col gap-3">
            <h4 class="text-xs font-bold text-slate-800 border-b border-slate-50 pb-2">
              SMS Details
            </h4>
            <div class="grid grid-cols-1 gap-2.5 text-xs">
              <div class="flex items-start">
                <span class="font-semibold text-slate-400 w-24 flex-shrink-0">Recipient:</span>
                <span class="text-slate-700 font-medium">{{ activeLog.mobile }}</span>
              </div>
              <div class="flex flex-col gap-1 mt-1">
                <span class="font-semibold text-slate-400">Message Content:</span>
                <div class="bg-slate-50 p-3 rounded-lg border border-slate-100 text-xs text-slate-800 leading-relaxed break-words whitespace-pre-wrap">
                  {{ activeLog.message }}
                </div>
              </div>
            </div>
          </div>

          <!-- Raw API Response Card -->
          <div v-if="activeLog.response" class="bg-white p-4 rounded-xl border border-slate-100 flex flex-col gap-3">
            <h4 class="text-xs font-bold text-slate-800 border-b border-slate-50 pb-2">
              Raw Provider Response
            </h4>
            <pre class="bg-slate-900 text-emerald-400 p-3 rounded-lg text-[10px] font-mono overflow-x-auto max-h-60 leading-normal">{{ JSON.stringify(activeLog.response, null, 2) }}</pre>
          </div>
        </v-card-text>

        <!-- Dialog Footer -->
        <v-card-actions class="bg-white border-t border-slate-100 p-4 justify-end rounded-b-xl">
          <v-btn
            color="indigo"
            class="normal-case rounded-lg text-xs"
            dark
            @click="detailsDialog = false"
          >
            Close View
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ImgSms from '@/assets/img/sms_icon.png'

export default {
  name: 'QuickberrySmsIntegration',

  data() {
    return {
      ImgSms,
      logs: [],
      loading: false,
      search: '',
      selectedStatus: '',

      page: 1,
      limit: 20,
      totalRecords: 0,

      detailsDialog: false,
      activeLog: {},

      statusOptions: [
        { text: 'All Statuses', value: '' },
        { text: 'Success', value: 'Success' },
        { text: 'Failed', value: 'Failed' },
        { text: 'Pending', value: 'Pending' }
      ]
    }
  },

  computed: {
    totalPages() {
      return Math.ceil(this.totalRecords / this.limit) || 1
    }
  },

  mounted() {
    this.fetchLogs()
  },

  methods: {
    async fetchLogs() {
      this.loading = true
      try {
        const params = {
          page: this.page,
          limit: this.limit
        }

        if (this.selectedStatus) {
          params.status = this.selectedStatus
        }

        if (this.search && this.search.trim()) {
          params.search = this.search.trim()
        }

        const response = await this.$axios.get('/quickberrySmsLogs', { params })
        if (response.data && response.data.success) {
          this.logs = response.data.data
          this.totalRecords = response.data.pagination.total
        }
      } catch (error) {
        console.error('Failed to fetch Quickberry SMS logs:', error)
        this.$toast.error('Failed to load Quickberry SMS logs')
      } finally {
        this.loading = false
      }
    },

    clearSearch() {
      this.search = ''
      this.page = 1
      this.fetchLogs()
    },

    changeLimit() {
      this.page = 1
      this.fetchLogs()
    },

    prevPage() {
      if (this.page > 1) {
        this.page--
        this.fetchLogs()
      }
    },

    nextPage() {
      if (this.page < this.totalPages) {
        this.page++
        this.fetchLogs()
      }
    },

    formatDateTime(dateString) {
      if (!dateString) return ''
      return new Date(dateString).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },

    getBadgeColor(status) {
      switch (status) {
        case 'Success':
          return 'emerald'
        case 'Pending':
          return 'amber darken-2'
        case 'Failed':
          return 'red darken-2'
        default:
          return 'grey'
      }
    },

    openDetails(log) {
      this.activeLog = log
      this.detailsDialog = true
    }
  }
}
</script>

<style scoped>
.text-gray-600 {
  color: #4b5563;
}
.bg-slate-50\/75 {
  background-color: rgba(248, 250, 252, 0.75);
}
.bg-slate-50\/50 {
  background-color: rgba(248, 250, 252, 0.5);
}
.v-simple-table >>> th {
  font-weight: 600 !important;
  color: #64748b !important;
  border-bottom: 1px solid #e2e8f0 !important;
}
.v-simple-table >>> td {
  border-bottom: 1px solid #f1f5f9 !important;
}
</style>
